import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import { Box, Popover, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { Options as QrCodeOptions } from 'qr-code-styling';
import useTimeout from "../../utils/hooks/useTimeout";
import useClipboard from "../../utils/hooks/useClipboard";
import AddToClipboardComponent from "../popups/Add-to-clipboard-component";
import { Colors } from "../../utils/theme";
import Showable from '../ui-v2/showable';

export type MakeAppQrCodeProps = {
	link: string;
	size?: number;
	showQrSquare?: boolean;
	hideCopyLinkButton?: boolean;
	qrCodeOptions?: QrCodeOptions;
	rootSx?: SxProps<Theme>;
	qrCodeWrapperSx?: SxProps<Theme>;
	qrCodeSx?: SxProps<Theme>;
};

const MakeAppQrCode: FC<MakeAppQrCodeProps> = (props) => {
	const {
		showQrSquare = true,
		link,
		size = 300,
		hideCopyLinkButton = false,
		qrCodeOptions = {},
		rootSx,
		qrCodeWrapperSx,
		qrCodeSx,
	} = props;
	const theme = useTheme();

	const qrRef = useRef(null);

	const [qrCode, setQrCode] = useState(null);

	const generateQrCode = useCallback(async () => {
		if (!showQrSquare) {
			return;
		}

		const { default: QRCodeStyling } = await import("qr-code-styling");

		const qrCode = new QRCodeStyling({
			width: size,
			height: size,
			dotsOptions: {
				color: Colors['393739'],
				// type: "dots",
				type: "extra-rounded",
			},
			cornersSquareOptions: {
				color: Colors['110A20'],
				type: "extra-rounded",
			},
			cornersDotOptions: {
				// type: 'dot',
			},
			qrOptions: {
				errorCorrectionLevel: 'H',
			},
			...qrCodeOptions,
		});

		setQrCode(qrCode);

		if (qrRef.current) {
			qrCode.append(qrRef.current);
		}
	}, []);

	useEffect(() => {
		generateQrCode();
  }, [generateQrCode]);

	useEffect(() => {
		qrCode?.update({
			data: link
		});
	}, [qrCode, link]);

	const [anchorEl, setAnchorEl] = useState(null);
	const { isOpen, onClose, onOpen} = useTimeout();
	const { copyTextToClipboard } = useClipboard();
	const linkRef = useRef(null);

	const addLinkToClipboard = async () => {
		await copyTextToClipboard(link);
		onOpen();
		setAnchorEl(linkRef.current);
	};

	const goToLink = () => {
		window.open(link, '_blank').focus();
	};

	return (
		<Box
			sx={[
				{
					display: 'flex',
					flexDirection: 'column',
					gap: '25px',
					justifyContent: 'center',
					alignItems: 'center'
				},
				...(Array.isArray(rootSx) ? rootSx : [rootSx]),
			]}
		>
			{showQrSquare && (
				<Box
					sx={[
						{
							p: '2px',
							backgroundColor: '#000',
							borderRadius: '20px',
							boxShadow: `2px 4px #000`
						},
						...(Array.isArray(qrCodeWrapperSx) ? qrCodeWrapperSx : [qrCodeWrapperSx]),
					]}
				>
					<Box
						sx={[
							{
								backgroundColor: theme.palette.primary.light,
								padding: '5px',
								borderRadius: '20px',
								overflow: 'hidden',
							},
							...(Array.isArray(qrCodeSx) ? qrCodeSx : [qrCodeSx]),
						]}
					>
						<div ref={qrRef} style={{ height: size, width: size }} />
					</Box>
				</Box>
			)}

			<Showable hide={hideCopyLinkButton}>
				<Box
					ref={linkRef}
					onClick={showQrSquare ? addLinkToClipboard : goToLink}
					sx={{
						backgroundColor: Colors['FAC23A'],
						py: '14px',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '4px',
						borderRadius: '30px',
						border: '3px solid #000',
						filter: 'drop-shadow(2px 3px 0px #000)',
						width: '100%',
						userSelect: 'none',
					}}
				>
					<Typography
						sx={{
							fontFamily: 'Nunito',
							fontWeight: 900,
							color: theme.palette.primary.shadow,
							fontSize: { xs: '14px', sm: '16px', lg: '20px' },
							lineHeight: 0.875,
						}}
					>
						Copy Link
					</Typography>
				</Box>
			</Showable>
			<Popover
				open={isOpen}
				anchorEl={anchorEl}
				onClose={onClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				PaperProps={{
					sx: {
						borderRadius: '30px',
						mt: '10px',
					},
				}}
			>
				<AddToClipboardComponent />
			</Popover>
		</Box>
	);
}

export default MakeAppQrCode;
